import { UserStoreService } from "../../../services/store/user/user.store.service";
import { CategoryView } from "./category.view";
import { CategoryStoreModel } from "../../store/category.store.model";
import {
  DEFAULT_CATEGORY_ID,
  DEFAULT_CATEGORY_NAME,
  GLOSS_CREDITS_CATEGORY_ID,
  GLOSS_CREDITS_CATEGORY_NAME,
} from "../../constants/category.constants";
import { checkDuplicateName } from "@bitwarden/web-vault/app/shared/utils/helper-new-view-name";

export class CategoryUtils {
  async getOrAddCategory(
    categoryName: string,
    userStoreService: UserStoreService,
  ): Promise<CategoryView> {
    const categories = userStoreService.categories.categoryViews() ?? [];
    const category = categories.find((cat) => cat.name === categoryName);

    if (!category) {
      const newCategory = new CategoryView();
      newCategory.name = categoryName;
      const isSaved = await userStoreService.categories.save(newCategory);
      return isSaved ? newCategory : null;
    }
    return category;
  }

  /**
   * Add a new category if it does not already exist
   * Returns null if the category already exists or if saving failed
   * @param categoryName
   * @param userStoreService
   */
  async addNewCategory(
    categoryName: string,
    userStoreService: UserStoreService,
  ): Promise<CategoryView> {
    if (!this.hasDuplicatedName(userStoreService, categoryName)) {
      const newCategory = new CategoryView();
      newCategory.name = categoryName;
      const isSaved = await userStoreService.categories.save(newCategory);
      if (isSaved && newCategory.id) {
        return newCategory;
      } else {
        throw new Error("Category could not be saved");
      }
    }
    throw new Error("duplicateNameError");
  }

  async updateCategoryName(
    newName: string,
    category: CategoryView,
    userStoreService: UserStoreService,
  ): Promise<CategoryView> {
    if (category.id === DEFAULT_CATEGORY_ID || category.id === GLOSS_CREDITS_CATEGORY_ID) {
      // Do not allow renaming the default or GLOSS Credits system category
      return null;
    }

    if (this.hasDuplicatedName(userStoreService, newName)) {
      throw new Error("duplicateNameError");
    }

    category.name = newName;
    const isSaved = await userStoreService.categories.save(category);
    return isSaved ? category : null;
  }

  static getDefaultCategoryModel() {
    const categoryStoreModel: CategoryStoreModel = {
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      gd: true,
      sc: "system",
      id: DEFAULT_CATEGORY_ID,
      n: DEFAULT_CATEGORY_NAME,
      v: 2,
      vid: null,
    };
    return categoryStoreModel;
  }

  static getDefaultCategoryView(): CategoryView {
    return new CategoryView(this.getDefaultCategoryModel());
  }

  static getGlossCreditsCategoryModel(): CategoryStoreModel {
    return {
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      gd: false,
      sc: "system",
      id: GLOSS_CREDITS_CATEGORY_ID,
      n: GLOSS_CREDITS_CATEGORY_NAME,
      v: 2,
      vid: null,
    };
  }

  static getGlossCreditsCategoryView(): CategoryView {
    return new CategoryView(this.getGlossCreditsCategoryModel());
  }

  hasDuplicatedName(userStoreService: UserStoreService, newCategoryName: string): boolean {
    return checkDuplicateName(userStoreService.categories.categoryViewsSorted(), newCategoryName);
  }
}
